//@flow
import {Store} from 'flux/utils';
import { Map } from 'immutable';
import Dispatcher from '../../dispatcher';
import Constants from '../../constants';
import { toImmutable } from '../../Utils/immutable';
import { cachedCreateObjectFromData, loadDataObject, getFlagProp, setFlagProp } from './storeUtils';

export type Error = {
  originalAction:string,
  type:string,
  message:string,
};

export type StoreState = Map<*, *>;

/**
Classes in ES6 by nature do not have private variables. To overcome this, we use a
Weakmap, a unique object in JS. We can store an instance of a store class as the key,
and the store's private data as its value. The key-value pair is garbage-collected when
the instance ceases to be referred to anywhere else. (As a result, we can now test stores,
repeatedly creating new instances to set up variable test conditions.)
*/
const _state:WeakMap<Store, StoreState> = new WeakMap();

function _getState(key:Store) {
  return _state.get(key) || Map();
}

function _setState(key:Store, state:StoreState) {
  _state.set(key, state);
}

function _setRawData(key:Store, data:Object) {
  _setState(key, toImmutable(data));
}

function _getRawData(key:Store) {
  return _getState(key).toJS();
}

function _setupInitialState(key:ImmutableStore, initialData:?Object) {
  const initialState = Map().withMutations(state => {
    if (initialData) state.mergeDeep(toImmutable(initialData));
    key._initialState(state);
  });
  _setState(key, initialState);
}

type UpdateRawDataCallback = (data:Object) => mixed;

export default class ImmutableStore extends Store {
  constructor(initialData?:Object) {
    super(Dispatcher);
    _setupInitialState(this, initialData);
  }

  _initialState(state:StoreState) { }

  // The superclass has a special dispatch function to unroll the current backwards
  // compatibility format.  If the event was an error, then we dispatch to the error
  // handler using the original action.  The error action (used as the main action
  // property for backwards compatibility) is contained in the error object as the
  // 'type' field for future use.
  //
  // The payload object should look like this
  //
  // {
  //   action: 'SOME_ACTION'
  //   data: <SOME_DATA_OBJECT> or APIResponseReader if it is an api result
  //
  //   if there is an error
  //   error: {
  //     originalAction: 'SOME_ACTION',
  //     type: 'ERROR',
  //     message: 'Some potentially helpful text'
  //   }
  // }
  __onDispatch(payload:{action:string, data:any, error:?Error}) {
    const { action = 'UNKNOWN', data = {}, error = null } = payload;

    if (error || action === Constants.ERROR) {
      const errorAction = (error && error.originalAction) || action;
      this.__onDispatchError(errorAction, error, data);
    }
    else {
      this.__onDispatchSuccess(action, data);
    }
  }

  // unless you want/need to deal with success/error in the same method,
  // override this method.
  //
  // action: 'SOME_ACTION'
  // data: <SOME_DATA_OBJECT> or APIResponseReader if it is an api result
  //
  __onDispatchSuccess(action:string, data:any) { }

  // if you want to deal with errors, override this method.  the difference
  // between this method and __onDispatch is that the action parameter will
  // be the same as would have been passed to __onDispatchSuccess if there
  // had been no error
  //
  // action: 'SOME_ACTION'
  // error:
  //   originalAction: 'SOME_ACTION'
  //   type: 'ERROR' or some other error type like NOT_AUTHORIZED
  //   message: 'some useful string'
  // data: <SOME_DATA_OBJECT>
  //
  __onDispatchError(action:string, error:?Error, data:any) { }

  getRawData():Object {
    return _getRawData(this);
  }

  getState():StoreState {
    return _getState(this);
  }

  // This method will clear out any existing data.
  // The should only be called during a dispatch because it will emit a
  // change event as a result.
  _reset(initialData:Object = {}):void {
    _setupInitialState(this, initialData);
    this.__emitChange();
  }

  // This method returns the value of the flag or the false if the flag does not exit.
  _getFlag(flag:string):boolean {
    return getFlagProp(this.getState(), flag);
  }

  // This method will set the value for a given flag.  Because of the fact
  // that it will trigger a state change, only call this method during a dispatch.
  _setFlag(flag:string, value:boolean):void {
    this._updateState((state:StoreState) => setFlagProp(state, flag, value));
  }

  _getProp(prop:string|number, defaultVal:any = null):any {
    return _getRawData(this)[String(prop)] || defaultVal;
  }

  _getObjectProp(prop:string):Object {
    return this._getProp(prop, {});
  }

  _resolvePropertyObject<T:Object>(path:string|string[], ObjClass:Class<T>, loadFunc:?() => any):T {
    return loadDataObject(this.getState(), path, (data:any) => cachedCreateObjectFromData(ObjClass, data), loadFunc);
  }

  // This method takes a single synchronus callback that is expected to
  // receive the data object as its first parameter and mutate as necessary.
  // Once the callback returns, the data will be persisted.  It should only
  // be called during a dispatch because it will emit a change event as a result.
  _updateRawData(callback:UpdateRawDataCallback):void {
    const newData = this.getRawData();
    callback(newData);
    _setRawData(this, newData);
    this.__emitChange();
  }

  _updateState(modifier:(Map<*,*>) => any):void {
    const originalState = this.getState();
    const newState = originalState.withMutations(modifier);
    if (originalState !== newState) {
      _setState(this, newState);
      this.__emitChange();
    }
  }
}
